import 'styles/custom.css';
import React from "react";
import { Table } from "react-bootstrap";
import { getHashedString, getRegionNames, updateTraceLogSettings, getCurrentLoggingSettings, updateGlobalTraceLogSettings } from "../components/Requests/Requests"
import { 
  StandardScreen,
  StandardDropdown, 
  StandardTextField, 
  StandardButton, 
  StatusMessage, 
  PageLayout
} from '../components/Layout';

class LoggingUtilities extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            //Variables for SpHost Logging utility
            spHostUrl: "",
            regionNameChosen: "",
            originalLogSettings: {
                'csomTraceEventLoggingEnabled': null,
                'graphTraceEventLoggingEnabled': null,
                'triggerTraceLoggingEnabled': null,
                'apiTraceLoggingEnabled': null,
                'redisCacheTraceLoggingEnabled': null,
                'mvcTraceLoggingEnabled': null,
                'memoryCacheTraceLoggingEnabled': null
            },
            logSettings: {
                'csomTraceEventLoggingEnabled': null,
                'graphTraceEventLoggingEnabled': null,
                'triggerTraceLoggingEnabled': null,
                'apiTraceLoggingEnabled': null,
                'redisCacheTraceLoggingEnabled': null,
                'mvcTraceLoggingEnabled': null,
                'memoryCacheTraceLoggingEnabled': null
            },
            currentSettingsMessage: "",
            logError: "",
            message: "",

            //Variables for Global Logging utility
            globalRegionNameChosen: "",
            globalOriginalLogSettings: {
                'csomTraceEventLoggingEnabled': null,
                'graphTraceEventLoggingEnabled': null,
                'triggerTraceLoggingEnabled': null,
                'apiTraceLoggingEnabled': null,
                'redisCacheTraceLoggingEnabled': null,
                'mvcTraceLoggingEnabled': null,
                'memoryCacheTraceLoggingEnabled': null
            },
            globalLogSettings: {
                'csomTraceEventLoggingEnabled': null,
                'graphTraceEventLoggingEnabled': null,
                'triggerTraceLoggingEnabled': null,
                'apiTraceLoggingEnabled': null,
                'redisCacheTraceLoggingEnabled': null,
                'mvcTraceLoggingEnabled': null,
                'memoryCacheTraceLoggingEnabled': null
            },
            globalSubmittedChanges: {},
            globalCurrentSettingsMessage: "",
            globalLogError: "",
            globalMessage: "",

            //Variables used for both Logging utilities
            regionNames: "",
            loggingFlags: [
                {
                    key: 'csomTraceEventLoggingEnabled',
                    label: 'CSOM Trace Event Logging',
                    bitValue: 1,
                    description: 'Logs a custom event whenever CSOM (client-side object model) is used to execute a query or action against SharePoint'
                },
                {
                    key: 'graphTraceEventLoggingEnabled',
                    label: 'Graph Trace Event Logging',
                    bitValue: 2,
                    description: 'Logs a custom event whenever an action is performed using the Graph API'
                },
                {
                    key: 'triggerTraceLoggingEnabled',
                    label: 'Trigger Trace Logging',
                    bitValue: 4,
                    description: 'Logs a custom event whenever an action is queued as a result of a trigger'
                },
                {
                    key: 'apiTraceLoggingEnabled',
                    label: 'API Trace Logging',
                    bitValue: 8,
                    description: 'Logs a custom event when one of the collaborate API endpoints is called'
                },
                {
                    key: 'redisCacheTraceLoggingEnabled',
                    label: 'Redis Cache Trace Logging',
                    bitValue: 16,
                    description: 'Logs custom events for items being added, removed, or retrieved from the redis cache, as well as cache invalidations.'
                },
                {
                    key: 'mvcTraceLoggingEnabled',
                    label: 'MVC Trace Logging',
                    bitValue: 64,
                    description: 'Logs a custom event when a controller action is called from the MVC application'
                },
                {
                    key: 'memoryCacheTraceLoggingEnabled',
                    label: 'Memory Cache Trace Logging',
                    bitValue: 128,
                    description: 'Logs custom events for items being added, removed, or retrieved from the memory cache, as well as cache invalidations.'
                }
            ],

            //Variables for Hashing utility
            hashedOutput: "",
            messageToHash: "",
            hashError: "",

            //Global variables for all utilities
            loading: false,
            showTable: false,
            error: ""
        }
    }

    // Utility methods
    parseBitFlagValue(bitFlagValue) {
        const settings = this.getInitialSettings();

        if (bitFlagValue === null || bitFlagValue === undefined) {
            return settings;
        }

        const bitFlags = {
            'csomTraceEventLoggingEnabled': 1,
            'graphTraceEventLoggingEnabled': 2,  
            'triggerTraceLoggingEnabled': 4,
            'apiTraceLoggingEnabled': 8,
            'redisCacheTraceLoggingEnabled': 16,
            'mvcTraceLoggingEnabled': 64,
            'memoryCacheTraceLoggingEnabled': 128
        };

        Object.keys(bitFlags).forEach(key => {
            const flagValue = bitFlags[key];
            settings[key] = (bitFlagValue & flagValue) !== 0;
        });

        return settings;
    }

    parseCurrentSettingsResponse(data) {
        if (data && typeof data === 'object' && data.hasOwnProperty('value')) {
            return this.parseBitFlagValue(data.value);
        } else {
            return this.getInitialSettings();
        }
    }

    getInitialSettings() {
        return {
            'csomTraceEventLoggingEnabled': null,
            'graphTraceEventLoggingEnabled': null,
            'triggerTraceLoggingEnabled': null,
            'apiTraceLoggingEnabled': null,
            'redisCacheTraceLoggingEnabled': null,
            'mvcTraceLoggingEnabled': null,
            'memoryCacheTraceLoggingEnabled': null
        };
    }

    getRegionNames() {
        getRegionNames(this.props.accessToken).then((response) => {
            if (response.status === 200) {
                response.text().then((data) => {
                    let dataArray = JSON.parse(data);
                    this.setState({ regionNames: dataArray.map((item) => { return { key: item, text: item } }), error: "" });
                });
            } else {
                this.setState({ logError: "Error when retrieving databases to connect to", message: "" });
            }
        });
    }

    // SpHost Logging utility methods
    async getCurrentLoggingSettings(){
        this.setState ({ loading: true})
        let requestBody = {};
        requestBody = {
            SPHostUrl: this.state.spHostUrl,
            RegionName: this.state.regionNameChosen
        };
        let response = await getCurrentLoggingSettings(this.props.accessToken, requestBody);
        let data = await response.json();
        if (response.status === 200){
            const currentSettings = this.parseCurrentSettingsResponse(data);
            this.setState({ 
                loading: false, 
                currentSettingsMessage: data.message || `Bit flag value: ${data.value !== null ? data.value : 'unknown'}`,
                originalLogSettings: { ...currentSettings },
                message: "",
                logSettings: { ...currentSettings }
            })
        }
        else if(response.status === 404){
            this.setState({ loading: false, message: data.message, currentSettingsMessage: "", logSettings: this.getInitialSettings(), originalLogSettings: this.getInitialSettings()});
        } 
        else{
            this.setState({ loading: false, error: "Something went wrong" });
        }
    }
    
    onSelectEnvironment = (event, item) => {
        this.setState({ 
            regionNameChosen: item.key,
            logError: "",
            message: "",
            currentSettingsMessage: ""
        });
    };

    onSelectGlobalEnvironment = (event, item) => {
        this.setState({ 
            globalRegionNameChosen: item.key,
            globalLogError: "",
            globalMessage: ""
        });
    };

    onSpHostUrlChange = (event, newValue) => {
        this.setState({ 
            spHostUrl: newValue,
            logError: "",
            message: "",
            currentSettingsMessage: ""
        });
    };

    onMessageToHashChange = (event, newValue) => {
        this.setState({ 
            messageToHash: newValue,
            hashError: "",
            hashedOutput: ""
        });
    };
    
    onCheckboxChange = (flagKey, isChecked) => {
        this.setState({
            logSettings: {
                ...this.state.logSettings,
                [flagKey]: isChecked
            }
        });
    };

    onGlobalCheckboxChange = (flagKey, value) => {
        const newSubmittedChanges = { ...this.state.globalSubmittedChanges };
        delete newSubmittedChanges[flagKey];
        
        this.setState({
            globalLogSettings: {
                ...this.state.globalLogSettings,
                [flagKey]: value
            },
            globalSubmittedChanges: newSubmittedChanges,
            globalLogError: "",
            globalMessage: ""
        });
    };

    resetToOriginalValues = () => {
        this.setState({
            logSettings: { ...this.state.originalLogSettings }
        });
    };

    resetGlobalToOriginalValues = () => {
        this.setState({
            globalLogSettings: { ...this.state.globalOriginalLogSettings }
        });
    };

    isValueChanged = (flagKey) => {
        return this.state.originalLogSettings[flagKey] !== this.state.logSettings[flagKey];
    };

    isGlobalValueChanged = (flagKey) => {
        return this.state.globalOriginalLogSettings[flagKey] !== this.state.globalLogSettings[flagKey];
    };

    hasAnyChanges = () => {
        return Object.keys(this.state.logSettings).some(key => 
            this.state.originalLogSettings[key] !== this.state.logSettings[key]
        );
    };

    hasAnyGlobalChanges = () => {
        return Object.keys(this.state.globalLogSettings).some(key => 
            this.state.globalOriginalLogSettings[key] !== this.state.globalLogSettings[key]
        );
    };

    getRowClassName = (flagKey) => {
        const isChanged = this.isValueChanged(flagKey);
        
        if (isChanged) {
            return 'logging-row-changed';
        }
        return '';
    };

    getGlobalRowClassName = (flagKey) => {
        const wasSubmitted = this.wasGlobalFlagSubmitted(flagKey);
        const submittedValue = this.getGlobalSubmittedValue(flagKey);
        
        if (wasSubmitted) {
            if (submittedValue === true) {
                return 'logging-row-submitted-enabled';
            } else if (submittedValue === false) {
                return 'logging-row-submitted-disabled';
            }
        }
        return '';
    };

    wasGlobalFlagSubmitted = (flagKey) => {
        return this.state.globalSubmittedChanges.hasOwnProperty(flagKey);
    };

    getGlobalSubmittedValue = (flagKey) => {
        return this.state.globalSubmittedChanges[flagKey];
    };

    constructAndSendRequest() {
        // Convert individual boolean flags to bit flag value
        const bitFlags = {
            'csomTraceEventLoggingEnabled': 1,
            'graphTraceEventLoggingEnabled': 2,
            'triggerTraceLoggingEnabled': 4,
            'apiTraceLoggingEnabled': 8,
            'redisCacheTraceLoggingEnabled': 16,
            'mvcTraceLoggingEnabled': 64,
            'memoryCacheTraceLoggingEnabled': 128
        };

        let combinedBitFlag = 0;
        Object.keys(bitFlags).forEach(key => {
            if (this.state.logSettings[key] === true) {
                combinedBitFlag |= bitFlags[key];
            }
        });

        let requestBody = {
            SPHostUrl: this.state.spHostUrl,
            RegionName: this.state.regionNameChosen,
            TraceLoggingFlagValue: combinedBitFlag
        };

        this.updateTraceLogSettings(requestBody);
    }

    constructAndSendGlobalRequest = () => {
        const bitFlags = {
            'csomTraceEventLoggingEnabled': 1,
            'graphTraceEventLoggingEnabled': 2,
            'triggerTraceLoggingEnabled': 4,
            'apiTraceLoggingEnabled': 8,
            'redisCacheTraceLoggingEnabled': 16,
            'mvcTraceLoggingEnabled': 64,
            'memoryCacheTraceLoggingEnabled': 128
        };
        
        const enabledFlags = Object.keys(bitFlags).filter(key => 
            this.state.globalLogSettings[key] === true
        );
        
        const disabledFlags = Object.keys(bitFlags).filter(key => 
            this.state.globalLogSettings[key] === false
        );

        let enabledBitFlag = 0;
        let disabledBitFlag = 0;

        enabledFlags.forEach(key => {
            enabledBitFlag |= bitFlags[key];
        });

        disabledFlags.forEach(key => {
            disabledBitFlag |= bitFlags[key];
        });

        let requestBody = {
            RegionName: this.state.globalRegionNameChosen,
            EnabledFlags: enabledBitFlag,
            DisabledFlags: disabledBitFlag
        };

        this.updateGlobalTraceLogSettings(requestBody);
    }

    updateTraceLogSettings(requestBody) {
        updateTraceLogSettings(this.props.accessToken, requestBody).then((response) => {
            if (response.status === 200) {
                response.json().then((data) => {
                    const updatedRows = data.UpdatedRows || data.updatedRows || 0;
                    
                    if (updatedRows > 0) {
                        this.setState({ 
                            logError: "",
                            message: "Settings updated successfully"
                        });
                        this.setState({
                            originalLogSettings: { ...this.state.logSettings }
                        });
                    } else {
                        this.setState({ 
                            logError: "No records updated, please check SPHostUrl", 
                            message: "" 
                        });
                    }
                });
            } else {
                this.setState({ logError: "Error when updating log settings", message: "" });
            }
        }).catch((error) => {
            this.setState({ logError: "Network error when updating log settings", message: "" });
        });
    }

    updateGlobalTraceLogSettings(requestBody) {
        const changesBeingSubmitted = {};
        Object.keys(this.state.globalLogSettings).forEach(key => {
            if (this.state.globalLogSettings[key] !== null && 
                this.state.globalOriginalLogSettings[key] !== this.state.globalLogSettings[key]) {
                changesBeingSubmitted[key] = this.state.globalLogSettings[key];
            }
        });

        updateGlobalTraceLogSettings(this.props.accessToken, requestBody).then((response) => {
            if (response.status === 200) {
                response.json().then((data) => {
                    this.setState({ 
                        globalLogError: "",
                        globalMessage: data.Message || "Global logging settings updated successfully",
                        globalSubmittedChanges: changesBeingSubmitted
                    });
                    this.setState({ 
                        globalOriginalLogSettings: { ...this.state.globalLogSettings } 
                    });
                }).catch((parseError) => {
                    this.setState({ 
                        globalLogError: "",
                        globalMessage: "Global logging settings updated successfully",
                        globalSubmittedChanges: changesBeingSubmitted
                    });
                    this.setState({ 
                        globalOriginalLogSettings: { ...this.state.globalLogSettings } 
                    });
                });
            }
            else if(response.status === 401){
                this.setState({ globalLogError: "You are not permitted to use this feature. You must request admin permissions", globalMessage: "" });
            }
            else{
                this.setState({ globalLogError: "Error when updating global log settings", globalMessage: "" });
            }
        }).catch((error) => {
            this.setState({ globalLogError: "Network error when updating global log settings", globalMessage: "" });
        });
    }
    
    // Hashing utility methods
    getHashedString(message) {
        getHashedString(this.props.accessToken, message).then((response) => {
            if (response.status === 200) {
                response.text().then((data) => {
                    this.setState({ hashedOutput: data, hashError: "" });
                });
            } else {
                this.setState({ hashError: "Something went wrong" });
            }
        });
    }

    //Lifecycle methods
    componentDidMount() {
        this.initializeSettings();
    }

    initializeSettings() {
        this.getRegionNames();
    }

    render() {
        const {
            loading,
            spHostUrl,
            regionNameChosen,
            logError,
            message,
            currentSettingsMessage,
            globalRegionNameChosen,
            globalLogError,
            globalMessage,
            messageToHash,
            hashedOutput,
            hashError,
            regionNames,
            loggingFlags,
            logSettings,
            originalLogSettings,
            globalLogSettings
        } = this.state;

        return (
            <PageLayout>
                <StandardScreen
                    title="Hashing Utility"
                    description="This tool will display the hashed version of the entered string. As some data is obfuscated in the log files, this can be used to check if a hashed string matches any in the logs."
                >
                    <div style={{ marginBottom: '20px' }}>                
                        <textarea
                            rows={10}
                            value={messageToHash}
                            onChange={(e) => this.onMessageToHashChange(e, e.target.value)}
                            style={{ 
                                width: "800px", 
                                padding: '10px', 
                                border: '1px solid #ddd', 
                                borderRadius: '4px',
                                fontFamily: '"Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
                                resize: 'none',
                            }}
                            placeholder="Enter text to hash..."
                        />
                    </div>

                    <StandardButton
                        text='Convert'
                        onClick={() => this.getHashedString(messageToHash)}
                        disabled={!messageToHash}
                    />

                    {hashError && (
                        <StatusMessage type="error">{hashError}</StatusMessage>
                    )}

                    {hashedOutput && (
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ 
                                display: 'block', 
                                marginBottom: '10px', 
                                fontWeight: 'bold',
                                fontSize: '12px',
                                color: '#000000ff'
                            }}>
                                Hashed Output:
                            </div>
                            <input 
                                type="text" 
                                value={hashedOutput} 
                                style={{ 
                                    width: "348px", 
                                    padding: '10px', 
                                    border: '1px solid #ddd', 
                                    borderRadius: '4px',
                                    backgroundColor: '#f9f9f9'
                                }} 
                                readOnly
                            />
                        </div>
                    )}

                    <div style={{ marginTop: '20px' }}>
                        <a 
                            href="https://logs.devops.intapp.com/app/discover/?security_tenant=global#/?_g=h@c823129&_a=h@0f321e1"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#0078d4', textDecoration: 'none' }}
                        >
                            Click here to view logs
                        </a>
                    </div>
                </StandardScreen>

                <StandardScreen
                    title="Toggle Logging for a Specific SPHostUrl"
                    description="This tool can be used to toggle types of logging in a selected database. Changed values will be highlighted with a yellow background."
                    loading={loading}
                >
                    <StandardTextField
                        label="SPHostUrl"
                        placeholder="Enter SPHostUrl"
                        value={spHostUrl}
                        onChange={this.onSpHostUrlChange}
                    />

                    <StandardDropdown
                        label="Environment"
                        selectedKey={regionNameChosen}
                        onChange={this.onSelectEnvironment}
                        placeholder="Select Environment"
                        options={regionNames}
                        disabled={!regionNames || regionNames.length === 0}
                    />

                    <StandardButton
                        text='Find Value'
                        disabled={!spHostUrl || !regionNameChosen}
                        onClick={() => this.getCurrentLoggingSettings()}
                        size = "wide"
                    />

                    {currentSettingsMessage && (
                        <div className="logging-table" style={{ marginTop: '20px' }}>
                            <Table striped bordered condensed hover>
                                <thead>
                                    <tr>
                                        <th>Logging Flag</th>
                                        <th style={{ width: '100px', textAlign: 'center' }}>Enabled</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loggingFlags.map((flag) => {
                                        const isChanged = this.isValueChanged(flag.key);
                                        const currentValue = logSettings[flag.key];
                                        const originalValue = originalLogSettings[flag.key];
                                        
                                        return (
                                            <tr 
                                                key={flag.key} 
                                                className={this.getRowClassName(flag.key)}
                                            >
                                                <td className="logging-flag-label">
                                                    {flag.label}
                                                    {isChanged && <span style={{ color: '#ffc107', marginLeft: '8px' }}>●</span>}
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={currentValue === true}
                                                        disabled={originalValue === null}
                                                        onChange={(e) => this.onCheckboxChange(flag.key, e.target.checked)}
                                                        className="logging-checkbox"
                                                        title={originalValue === null ? 'Current value unknown - fetch current values first' : ''}
                                                    />
                                                    {originalValue === null && (
                                                        <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>
                                                            Unknown
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="logging-description">
                                                    {flag.description}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                            {this.hasAnyChanges() && (
                                <div style={{ 
                                    padding: '10px', 
                                    backgroundColor: '#d1ecf1', 
                                    border: '1px solid #bee5eb', 
                                    borderRadius: '4px',
                                    marginTop: '10px'
                                }}>
                                    <small>
                                        <strong>Legend:</strong>
                                        <span> Yellow background with ● = modified from original values (pending changes)</span>
                                    </small>
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '20px' }}>
                        <StandardButton 
                            text='Submit Changes' 
                            onClick={() => this.constructAndSendRequest()} 
                            disabled={!spHostUrl || !regionNameChosen || !currentSettingsMessage || !this.hasAnyChanges()} 
                            size = "wide"
                        />
                        {this.hasAnyChanges() && (
                            <StandardButton
                                text='Reset Changes'
                                onClick={this.resetToOriginalValues}
                                size = "wide"
                            >
                            </StandardButton>
                        )}
                    </div>

                    {logError && (
                        <StatusMessage type="error">{logError}</StatusMessage>
                    )}

                    {message && (
                        <StatusMessage type="success">{message}</StatusMessage>
                    )}
                </StandardScreen>

                <StandardScreen
                    title="Toggle Logging Globally"
                    description="This tool can be used to toggle types of logging globally in a selected environment. This is an admin only feature."
                >
                    <StandardDropdown
                        label="Environment"
                        selectedKey={globalRegionNameChosen}
                        onChange={this.onSelectGlobalEnvironment}
                        placeholder="Select Environment"
                        options={regionNames}
                        disabled={!regionNames || regionNames.length === 0}
                    />

                    <div className="logging-table" style={{ marginTop: '20px' }}>
                        <Table striped bordered condensed hover>
                            <thead>
                                <tr>
                                    <th>Logging Flag</th>
                                    <th style={{ width: '150px', textAlign: 'center' }}>Status</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loggingFlags.map((flag) => {
                                    const currentValue = globalLogSettings[flag.key];
                                    return (
                                        <tr 
                                            key={flag.key} 
                                            className={this.getGlobalRowClassName(flag.key)}
                                        >
                                            <td className="logging-flag-label">
                                                {flag.label}
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px' }}>
                                                    <label style={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        fontSize: '12px', 
                                                        cursor: 'pointer'
                                                    }}>
                                                        <input
                                                            type="radio"
                                                            name={`global-${flag.key}`}
                                                            checked={currentValue === true}
                                                            onChange={() => this.onGlobalCheckboxChange(flag.key, true)}
                                                            style={{ marginRight: '4px' }}
                                                        />
                                                        Enabled
                                                    </label>
                                                    <label style={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        fontSize: '12px', 
                                                        cursor: 'pointer'
                                                    }}>
                                                        <input
                                                            type="radio"
                                                            name={`global-${flag.key}`}
                                                            checked={currentValue === false}
                                                            onChange={() => this.onGlobalCheckboxChange(flag.key, false)}
                                                            style={{ marginRight: '4px' }}
                                                        />
                                                        Disabled
                                                    </label>
                                                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '12px', cursor: 'pointer' }}>
                                                        <input
                                                            type="radio"
                                                            name={`global-${flag.key}`}
                                                            checked={currentValue === null}
                                                            onChange={() => this.onGlobalCheckboxChange(flag.key, null)}
                                                            style={{ marginRight: '4px' }}
                                                        />
                                                        Unchanged
                                                    </label>
                                                </div>
                                            </td>
                                            <td className="logging-description">
                                                {flag.description}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>

                    <StandardButton 
                        text='Submit Changes' 
                        onClick={() => {
                            this.constructAndSendGlobalRequest();
                            this.resetGlobalToOriginalValues();
                        }} 
                        disabled={!globalRegionNameChosen || !this.hasAnyGlobalChanges()} 
                        size = "wide"
                    />

                    {globalLogError && (
                        <StatusMessage type="error">{globalLogError}</StatusMessage>
                    )}

                    {globalMessage && (
                        <StatusMessage type="success">{globalMessage}</StatusMessage>
                    )}
                </StandardScreen>
            </PageLayout>
        );
    }
}

export default LoggingUtilities;